import ApiServiceV2 from './apiService/apiServiceV2';

const BUCKET = 'av4cus-synapse-pilot-assets-270254976539-use2';
const MAX_BYTES = 32 * 1024 * 1024;
const FRESH_HOSTS = [BUCKET + '.s3.us-east-2.amazonaws.com', BUCKET + '.s3.amazonaws.com'];
const LEGACY_HOSTS = ['syn4pse.s3.amazonaws.com', 'syn4pse.s3.us-east-1.amazonaws.com', 'syn4pse.s3-us-east-1.amazonaws.com'];
const LEGACY_PATH_HOSTS = ['s3.amazonaws.com', 's3.us-east-1.amazonaws.com', 's3-us-east-1.amazonaws.com'];

// Identifying the private origin is separate from accepting a canonical reference.
// Invalid Fresh references must stay on the private/blocked branch, never legacy.
export function hasFreshCollectorPhotoOrigin(src) {
  if (typeof src !== 'string') return false;
  try { return FRESH_HOSTS.includes(new URL(src, 'https://photo-reference.invalid').hostname); }
  catch (_) {
    const authority = /^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/?#]*)/i.exec(src.trim());
    const host = authority && authority[1].split('@').pop().split(':')[0].toLowerCase();
    return FRESH_HOSTS.includes(host);
  }
}
export function isFreshCollectorPhotoSrc(src) {
  if (typeof src !== 'string' || src.trim() !== src || /[%\\\s?#]/.test(src) ||
    /(?:^|\/)\.{1,2}(?:\/|$)/.test(src)) return false;
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.search && !url.hash &&
      FRESH_HOSTS.includes(url.hostname) &&
      /^\/(production|development)\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/(?:[A-Za-z0-9_-]+\/)+[A-Za-z0-9_-]+\.jpg$/.test(url.pathname);
  } catch (_) { return false; }
}

// Routing only: the API resolves storage and tenant ACLs from the native row ID.
// Never forward the persisted URL or attach a Bearer to its storage origin.
export function collectorPhotoRoute(src) {
  if (hasFreshCollectorPhotoOrigin(src)) return isFreshCollectorPhotoSrc(src) ? 'private' : 'blocked';
  if (typeof src !== 'string') return 'legacy';
  let url;
  try { url = new URL(src, 'https://photo-reference.invalid'); } catch (_) { /* raw origin below */ }
  const raw = /^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/?#]*)([^?#]*)/i.exec(src.trim());
  const host = url ? url.hostname : raw && raw[1].split('@').pop().split(':')[0].toLowerCase();
  const rawPath = raw ? raw[2] : '';
  let decodedPath = rawPath;
  try { decodedPath = decodeURIComponent(rawPath); } catch (_) { /* rejected below */ }
  const pathStyle = LEGACY_PATH_HOSTS.includes(host) &&
    [rawPath, decodedPath, url && url.pathname].some(path => /^\/syn4pse(?:\/|$)/.test(path || ''));
  if (!LEGACY_HOSTS.includes(host) && !pathStyle) return 'legacy';
  if (!url || src.trim() !== src || /[%\\\s?#]/.test(src) ||
    /(?:^|\/)\.{1,2}(?:\/|$)/.test(src) ||
    url.protocol !== 'https:' || url.username || url.password || url.port ||
    src !== 'https://' + url.hostname + url.pathname) return 'blocked';
  const path = pathStyle ? url.pathname.slice('/syn4pse'.length) : url.pathname;
  return /^\/(production|development)\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/photoCollectors\/[A-Za-z0-9_-]+\.jpg$/.test(path)
    ? 'private' : 'blocked';
}

// Per-component Blob lease: no stored token, private URL, or cross-user cache.
export function loadPrivateCollectorPhoto({ photoId, variant = '70', api = ApiServiceV2,
  urls = URL, Controller = AbortController, timeoutMs = 20000 } = {}) {
  if (!/^[1-9][0-9]*$/.test(String(photoId)) || !Number.isSafeInteger(Number(photoId)) ||
    !['original', '70', '150', '400'].includes(String(variant))) throw new Error('PRIVATE_PHOTO_UNAVAILABLE');
  const controller = new Controller();
  let objectUrl = null, cancelled = false;
  const cancel = () => {
    cancelled = true; controller.abort(); clearTimeout(timer);
    if (objectUrl) urls.revokeObjectURL(objectUrl);
    objectUrl = null;
  };
  const timer = setTimeout(cancel, timeoutMs);
  const done = (async () => {
    try {
      const blob = await api.getImageBlob({ url: `/collector_values/photos/${photoId}/${variant}`, controlSignal: controller.signal });
      if (cancelled || !blob || !['image/jpeg', 'image/png'].includes(blob.type) || blob.size < 1 || blob.size > MAX_BYTES)
        throw new Error('PRIVATE_PHOTO_UNAVAILABLE');
      objectUrl = urls.createObjectURL(blob);
      return objectUrl;
    } catch (_) {
      cancel(); throw new Error('PRIVATE_PHOTO_UNAVAILABLE');
    } finally { clearTimeout(timer); }
  })();
  return { done, cancel };
}
